/**
 * Google Analytics (GA4) integration via Firebase Analytics
 *
 * Analytics is initialized lazily on the first tracked event so the
 * firebase/analytics chunk stays out of the critical path. Automatic
 * page_view collection is turned off because gtag only fires it on the
 * initial document load — client-side route changes are reported by
 * usePageTracking instead, which keeps every route counted exactly once.
 *
 * Nothing here runs before the user opts in: GA4 sets cookies, so the SDK is
 * not even loaded until the consent store reports 'granted'.
 */

// Type-only: importing the SDK for real would pull it into the main bundle
// even for visitors who never consent. It is loaded via dynamic import below.
import type { Analytics } from 'firebase/analytics';
import { app } from '../firebase';
import { hasAnalyticsConsent, subscribeConsent } from './cookieConsent';
import { logger } from './logger';

/**
 * Routes whose path segments carry Firestore document IDs. Reporting the
 * raw path would push internal identifiers into GA4, so they are replaced
 * with the route pattern before anything leaves the browser.
 */
const DYNAMIC_ROUTES: Array<[RegExp, string]> = [
  [/^\/edit-receipt\/[^/]+$/, '/edit-receipt/:id'],
  [/^\/clients\/[^/]+$/, '/clients/:clientId'],
];

/**
 * Normalize a pathname into a stable, ID-free page path.
 */
export const sanitizePath = (pathname: string): string => {
  const path = pathname || '/';
  const match = DYNAMIC_ROUTES.find(([pattern]) => pattern.test(path));
  return match ? match[1] : path;
};

let analyticsPromise: Promise<Analytics | null> | null = null;

const initAnalytics = async (): Promise<Analytics | null> => {
  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

  if (!hasAnalyticsConsent()) {
    logger.debug('Analytics skipped (no cookie consent)', undefined, {
      component: 'Analytics',
      operation: 'Initialize',
    });
    return null;
  }

  if (!measurementId) {
    logger.debug(
      'Analytics not configured (VITE_FIREBASE_MEASUREMENT_ID not set)',
      undefined,
      { component: 'Analytics', operation: 'Initialize' }
    );
    return null;
  }

  try {
    const { initializeAnalytics, isSupported } = await import(
      'firebase/analytics'
    );

    if (!(await isSupported())) {
      logger.debug('Analytics not supported in this browser', undefined, {
        component: 'Analytics',
        operation: 'Initialize',
      });
      return null;
    }

    const analytics = initializeAnalytics(app, {
      config: { send_page_view: false },
    });

    logger.info('Analytics initialized', {
      component: 'Analytics',
      operation: 'Initialize',
    });

    return analytics;
  } catch (error) {
    logger.warn('Analytics initialization failed', error as Error, {
      component: 'Analytics',
      operation: 'Initialize',
    });
    return null;
  }
};

/**
 * Resolve the Analytics instance, initializing it on first use.
 * Resolves to null when consent is missing, or Analytics is unconfigured or
 * unsupported.
 */
export const getAnalyticsInstance = (): Promise<Analytics | null> => {
  if (!analyticsPromise) {
    analyticsPromise = initAnalytics();
  }
  return analyticsPromise;
};

/**
 * Mirror a consent change onto an SDK that is already running. gtag keeps
 * collecting on its own (session_start, user_engagement) once loaded, so
 * withdrawing consent has to switch collection off rather than just stop
 * our own events. Never initializes the SDK — if it was never loaded there
 * is nothing to disable.
 */
const applyConsentToLoadedSdk = async (): Promise<void> => {
  if (!analyticsPromise) {
    return;
  }

  const analytics = await analyticsPromise;
  if (!analytics) {
    // Consent may have arrived after a failed/denied init — retry next event
    analyticsPromise = null;
    return;
  }

  try {
    const { setAnalyticsCollectionEnabled } = await import(
      'firebase/analytics'
    );
    setAnalyticsCollectionEnabled(analytics, hasAnalyticsConsent());
  } catch (error) {
    logger.warn('Applying analytics consent failed', error as Error, {
      component: 'Analytics',
      operation: 'Consent',
    });
  }
};

subscribeConsent(() => {
  void applyConsentToLoadedSdk();
});

/**
 * Report a page view to GA4. Never throws — analytics must not be able to
 * break navigation.
 */
export const trackPageView = async (
  pathname: string,
  title?: string
): Promise<void> => {
  if (!hasAnalyticsConsent()) {
    return;
  }

  const analytics = await getAnalyticsInstance();
  if (!analytics) {
    return;
  }

  const pagePath = sanitizePath(pathname);

  try {
    const { logEvent } = await import('firebase/analytics');

    logEvent(analytics, 'page_view', {
      page_path: pagePath,
      page_location: `${window.location.origin}${pagePath}`,
      page_title: title ?? document.title,
    });

    logger.debug(
      'Page view tracked',
      { pagePath },
      {
        component: 'Analytics',
        operation: 'PageView',
      }
    );
  } catch (error) {
    logger.warn('Page view tracking failed', error as Error, {
      component: 'Analytics',
      operation: 'PageView',
    });
  }
};
